'use client';

import { type FileDropConfig } from '@magicschool/spellbook/components/Dropzone';
import { useSlots } from '@magicschool/spellbook/hooks/useSlots';
import { cn } from '@magicschool/spellbook/lib/utils';
import { useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import {
  ChatInputAttachmentsSlot,
  ChatInputContextSlot,
  ChatInputDropOverlaySlot,
  ChatInputFootnoteSlot,
  ChatInputLeadingAdornmentSlot,
  ChatInputMenuItem,
  type ChatInputMenuItemProps,
  ChatInputMenuSlot,
  type ChatInputMenuSlotProps,
  ChatInputSendButtonSlot,
  type ChatInputSendButtonSlotProps,
  ChatInputStopButtonSlot,
  type ChatInputStopButtonSlotProps,
  type ChatInputTextareaProps,
  ChatInputTextareaSlot,
  ChatInputToolbarEndSlot,
  ChatInputToolbarStartSlot,
} from './slots';
import { useFileDrop } from './useFileDrop';

// Radix portals render into document.body (not a descendant), so focus moving into one stays "inside".
function isFocusLeavingCard(root: HTMLElement, relatedTarget: EventTarget | null) {
  const next = relatedTarget instanceof HTMLElement ? relatedTarget : null;
  return !root.contains(next) && !next?.closest('[data-radix-popper-content-wrapper]');
}

function rescueFocusToTextarea(root: HTMLElement | null | undefined) {
  if (!root || typeof document === 'undefined') return;
  if (document.activeElement !== document.body) return;
  root.querySelector<HTMLTextAreaElement>('textarea')?.focus({ preventScroll: true });
}

export type {
  ChatInputMenuItemProps,
  ChatInputMenuSlotProps as ChatInputMenuProps,
  ChatInputSendButtonSlotProps as ChatInputSendButtonProps,
  ChatInputStopButtonSlotProps as ChatInputStopButtonProps,
  ChatInputTextareaProps,
};

export interface ChatInputFileDropConfig extends FileDropConfig {
  /** Force drag-active state — useful for visual review without a real drag. */
  isDragActive?: boolean;
  /** Force drag-accept visual state. Requires `isDragActive`. */
  isDragAccept?: boolean;
  /** Force drag-reject visual state. Requires `isDragActive`. */
  isDragReject?: boolean;
}

export interface ChatInputProps extends React.ComponentProps<'div'> {
  /** Minimum input length. Defaults to `0`. */
  minLength?: number;
  /** Maximum input length. Defaults to `Infinity` unless overriden by the tool. */
  maxLength?: number;
  /** Disables the send button and attachment menu while leaving the textarea interactive — use during AI streaming so users can draft their next message. */
  disabled?: boolean;
  /** Called when focus enters or leaves the input container. Use to drive page-level blur effects. */
  onFocusChange?: (focused: boolean) => void;
  /**
   * Typewriter animation shown when the input is empty and unfocused. Respects `prefers-reduced-motion`.
   *
   * - `prompts` — rotating suggestion strings
   * - `ariaDescription` — SR-only description read once on focus; defaults to the first 3 prompts joined by ", "
   * - `onChange` — fired when the typewriter starts a new prompt; stabilize with `useCallback`
   */
  animatedPlaceholder?: {
    prompts: string[];
    ariaDescription?: string;
    onChange?: (text: string, index: number) => void;
  };
  /**
   * Surface shadow recipe. `'none'` (default) | `'elevated'` (hero/landing).
   * Resolves from `--shadow-chat-elevated` in theme.css.
   */
  shadow?: 'none' | 'elevated';
  /** Enables drag-and-drop file uploads. Render `<ChatInput.DropOverlay>` for a custom drag visual. */
  fileDrop?: ChatInputFileDropConfig;
  /**
   * On a short viewport (under 30rem tall, as at 400% zoom), folds the toolbar row away until focus is
   * inside the card, so content above keeps room to read. Defaults to `false`. Ignored while
   * `<ChatInput.StopButton>` renders; pass `false` whenever another toolbar control must stay reachable
   * (a live recording, a disabled textarea that can no longer unfold it).
   */
  collapseToolbarWhenShort?: boolean;
}

const ChatInputBase = ({
  children,
  className,
  disabled,
  onFocusChange,
  onFocus,
  onBlur,
  onMouseDown,
  animatedPlaceholder,
  shadow = 'none',
  fileDrop: fileDropProp,
  collapseToolbarWhenShort = false,
  minLength,
  maxLength,
  ...props
}: ChatInputProps) => {
  const {
    isDragActive: isDragActiveProp,
    isDragAccept: isDragAcceptProp,
    isDragReject: isDragRejectProp,
    ...fileDrop
  } = fileDropProp ?? {};
  const resolvedFileDrop = fileDropProp ? fileDrop : undefined;
  // Hoisted so the open state survives child remounts (e.g. focus-trap dialogs).
  const [attachmentsOpen, setAttachmentsOpen] = useState(true);
  // Tracked in JS rather than CSS `:focus-within`, which misses focus inside a portaled menu.
  const [hasFocusWithin, setHasFocusWithin] = useState(false);

  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLDivElement>) => {
      if (isFocusLeavingCard(e.currentTarget, e.relatedTarget)) onBlur?.(e);
    },
    [onBlur],
  );

  // Chip X has no slot transition — arm double-rAF rescue on mousedown.
  const scheduleFocusRescue = useCallback((root: HTMLElement) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        rescueFocusToTextarea(root);
      });
    });
  }, []);

  const handleWrapperMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      scheduleFocusRescue(e.currentTarget);
      onMouseDown?.(e);
    },
    [onMouseDown, scheduleFocusRescue],
  );

  // Extract ref and onInput before spread: ref would be silently overridden by getRootProps, and
  // onInput is composed manually below so we can clear drop errors on typing without clobbering
  // the consumer's handler.
  const { ref: consumerRef, onInput: consumerOnInput, ...restProps } = props;
  const internalRootRef = useRef<HTMLDivElement>(null);

  // Native listeners: a child's React `stopPropagation()` (e.g. a toggletip trigger) hides its
  // focus and blur from React handlers on this root, but not from `focusin`/`focusout`.
  useEffect(() => {
    const root = internalRootRef.current;
    if (!root) return;
    const handleFocusIn = (e: FocusEvent) => {
      setHasFocusWithin(true);
      // Toolbar control clicks (e.g. the + menu) must not activate focus mode.
      if ((e.target as HTMLElement).tagName === 'TEXTAREA') onFocusChange?.(true);
    };
    // Safari does not focus a clicked button, so pressing Send blurs the card before the click lands.
    // Folding then would move Send out from under the pointer; wait for the press to end instead.
    let pressing = false;
    let foldAfterPress: ReturnType<typeof setTimeout> | undefined;
    const handleFocusOut = (e: FocusEvent) => {
      if (!isFocusLeavingCard(root, e.relatedTarget)) return;
      if (!pressing) setHasFocusWithin(false);
      onFocusChange?.(false);
    };
    const handlePointerDown = () => {
      pressing = true;
    };
    const handlePointerUp = () => {
      if (!pressing) return;
      pressing = false;
      // A timeout, so the click this press makes is dispatched first.
      foldAfterPress = setTimeout(() => {
        if (isFocusLeavingCard(root, document.activeElement)) setHasFocusWithin(false);
      });
    };
    root.addEventListener('focusin', handleFocusIn);
    root.addEventListener('focusout', handleFocusOut);
    root.addEventListener('pointerdown', handlePointerDown, true);
    window.addEventListener('pointerup', handlePointerUp, true);
    window.addEventListener('pointercancel', handlePointerUp, true);
    return () => {
      root.removeEventListener('focusin', handleFocusIn);
      root.removeEventListener('focusout', handleFocusOut);
      root.removeEventListener('pointerdown', handlePointerDown, true);
      window.removeEventListener('pointerup', handlePointerUp, true);
      window.removeEventListener('pointercancel', handlePointerUp, true);
      clearTimeout(foldAfterPress);
    };
  }, [onFocusChange]);

  const {
    getDropRootProps,
    dropzoneRootRef,
    isDragActive,
    isDragAccept,
    isDragReject,
    dropError,
    clearDropError,
    dragAnnouncement,
    statusMessage,
  } = useFileDrop({
    fileDrop: resolvedFileDrop,
    disabled,
    isDragActiveProp,
    isDragAcceptProp,
    isDragRejectProp,
    containerRef: internalRootRef,
  });

  const { slots } = useSlots(children, {
    context: { Component: ChatInputContextSlot, props: {} },
    leadingAdornment: { Component: ChatInputLeadingAdornmentSlot, props: {} },
    textarea: {
      Component: ChatInputTextareaSlot,
      props: { animatedPlaceholder, minLength, maxLength },
    },
    attachments: { Component: ChatInputAttachmentsSlot, props: { open: attachmentsOpen, onOpenChange: setAttachmentsOpen } },
    menu: { Component: ChatInputMenuSlot, props: { disabled } },
    toolbarStart: { Component: ChatInputToolbarStartSlot, props: {} },
    toolbarEnd: { Component: ChatInputToolbarEndSlot, props: {} },
    sendButton: { Component: ChatInputSendButtonSlot, props: { rootDisabled: disabled } },
    // Omit rootDisabled so Stop stays clickable during streaming.
    stopButton: { Component: ChatInputStopButtonSlot, props: {} },
    footnote: { Component: ChatInputFootnoteSlot, props: { error: dropError, onErrorDismiss: dropError ? clearDropError : undefined } },
    dropOverlay: { Component: ChatInputDropOverlaySlot, props: { isDragActive, isDragAccept, isDragReject } },
  });

  const hasStopChild = !!slots.stopButton;
  const showSendButton = !hasStopChild;
  // Never folded while Stop is offered: aborting a turn must stay one click away.
  const toolbarCollapsed = collapseToolbarWhenShort && !hasStopChild && !hasFocusWithin;

  // Stop true→false: restore textarea if focus fell to body (mouse/keyboard/stream end).
  const hadStopChildRef = useRef(false);
  useEffect(() => {
    const hadStop = hadStopChildRef.current;
    hadStopChildRef.current = hasStopChild;
    if (!hadStop || hasStopChild) return;
    rescueFocusToTextarea(internalRootRef.current);
  }, [hasStopChild]);

  const dropRootProps = getDropRootProps(restProps);
  useImperativeHandle(dropzoneRootRef, () => internalRootRef.current!);
  useImperativeHandle(consumerRef, () => internalRootRef.current!);

  // When fileDrop is active, clear any stale rejection error as soon as the user starts typing.
  // Compose with the consumer's onInput so both handlers fire.
  const handleInput = useCallback(
    (e: React.FormEvent<HTMLDivElement>) => {
      if (resolvedFileDrop) clearDropError();
      consumerOnInput?.(e as React.FormEvent<HTMLDivElement>);
    },
    [resolvedFileDrop, clearDropError, consumerOnInput],
  );

  return (
    <div data-spellbook-name="chat-input" className="w-full @container">
      <div
        {...dropRootProps}
        ref={internalRootRef}
        onFocus={onFocus}
        onBlur={handleBlur}
        onMouseDown={handleWrapperMouseDown}
        onInput={handleInput}
        className={cn(
          'relative w-full bg-surface-background-0 rounded-lg border border-chat-input-border-default',

          shadow === 'elevated' && 'shadow-chat-elevated',
          'hover:border-chat-input-border-hover',
          'focus-within:border-chat-input-border-focus hover:focus-within:border-chat-input-border-focus',
          'transition-colors duration-200',
          className,
        )}
      >
        {/* inert during drag so keyboard focus cannot reach the textarea or toolbar
            buttons behind the DropOverlay. The overlay itself lives outside this
            wrapper and remains fully interactive / visible. */}
        <div inert={isDragActive || undefined}>
          {slots.context}
          <div
            className={cn('flex items-center gap-2 px-3 py-3 @md:px-4 @md:pt-4 @md:pb-4 cursor-text', slots.context && 'pt-2 @md:pt-2')}
            onMouseDown={(e) => {
              if ((e.target as HTMLElement).tagName === 'TEXTAREA') return;
              e.preventDefault();
              e.currentTarget.querySelector('textarea')?.focus();
            }}
          >
            {slots.leadingAdornment}
            <div className="flex-1 min-w-0">{slots.textarea}</div>
          </div>
          {slots.attachments}
          {/* A grid-rows collapse rather than `display`, so the row can ease open (motion-safe only). */}
          <div
            data-toolbar-collapsed={toolbarCollapsed || undefined}
            className={cn(
              'grid motion-safe:transition-[grid-template-rows] motion-safe:duration-200 motion-safe:ease-out',
              // Both ends explicit: a transition cannot interpolate from the implicit `none`.
              toolbarCollapsed ? 'short:grid-rows-[0fr]' : 'short:grid-rows-[1fr]',
            )}
          >
            <div data-spellbook-name="chat-input-toolbar" className="min-h-0 short:overflow-hidden">
              <div
                className="flex items-center justify-between p-2 @md:p-3"
                onMouseDown={(e) => {
                  // Prevent non-interactive toolbar area clicks from blurring the textarea.
                  // Interactive elements handle their own focus; only suppress on the background.
                  if (!(e.target as HTMLElement).closest('button, a, input, select, textarea, [role="button"], [tabindex]')) {
                    e.preventDefault();
                  }
                }}
              >
                <div className="flex items-center gap-2">
                  {slots.menu}
                  {slots.toolbarStart}
                </div>
                <div className="flex items-center gap-2">
                  {slots.toolbarEnd}
                  {slots.stopButton}
                  {showSendButton ? slots.sendButton : null}
                </div>
              </div>
            </div>
          </div>
        </div>
        {slots.dropOverlay}
        {/* Live region for drag/drop announcements. aria-atomic re-reads the full message on
            any update so SR users hear "3 added, 2 rejected" as one phrase. Drag announcements
            take priority while a drag is active; the last status message persists after the drag ends. */}
        {resolvedFileDrop && (
          <output aria-live="polite" aria-atomic="true" className="sr-only">
            {dragAnnouncement ?? statusMessage}
          </output>
        )}
      </div>
      {slots.footnote}
    </div>
  );
};
ChatInputBase.displayName = 'ChatInput';

/**
 * Card-chrome organism for chat input surfaces. Composes a bordered card wrapper, auto-resizing textarea,
 * attachment previews, toolbar, and optional animated placeholder prompts via slot children.
 *
 * ### When to use
 * - Chat or prompt inputs that need a card-style bordered container with textarea and toolbar controls
 * - Surfaces that benefit from typewriter-animated placeholder prompts to aid prompt discovery
 * - Inputs that accept file attachments via drag-and-drop or a picker menu
 *
 * ### When NOT to use
 * - Simple single-line text entry: use `TextInput` instead
 * - Form textarea without toolbar chrome: use `Textarea` directly
 *
 * ### API
 * - `shadow`: Card shadow recipe — `'none'` (default), `'elevated'` (hero/landing)
 * - `animatedPlaceholder`: Typewriter animation config (`prompts`, `ariaDescription`, `onChange`)
 * - `onFocusChange`: Fires `true`/`false` when the textarea is focused or blurred — use to drive focus-mode overlays. Only fires for the textarea, not toolbar controls.
 * - `fileDrop`: Enables drag-and-drop file intake; pair with `<ChatInput.Menu>` for keyboard-accessible picking
 * - `collapseToolbarWhenShort`: Opt-in (default `false`). On a short viewport, folds the toolbar row until focus is inside the card. For a composer under a transcript; pass `false` while a toolbar control must stay reachable
 * - `disabled`: Disables the send button and attachment menu; the textarea stays interactive so users can draft their next message during AI streaming. Does **not** disable `<ChatInput.StopButton>` — Stop remains interactive so consumers can abort an in-flight turn.
 *
 * ### Slots
 * - `<ChatInput.Context>` — chip row at the top of the card for what the message is ABOUT (a selected slide); distinct from `ToolbarStart`, which holds settings the turn runs with
 * - `<ChatInput.Textarea>` — auto-resizing textarea; pass `value`/`onChange` for controlled use
 * - `<ChatInput.LeadingAdornment>` — flex container before the textarea (avatar, icon)
 * - `<ChatInput.Attachments label="3 files">` — collapsible chip row; pass `FilePreview` chips as children
 * - `<ChatInput.Menu>` — `+` button opening a `DropdownMenu` for file picker sources
 * - `<ChatInput.ToolbarStart>` / `<ChatInput.ToolbarEnd>` — left / right toolbar slots
 * - `<ChatInput.SendButton>` — enforces the canonical `arrow-up` send button; accepts `disabled`, `onClick`, and `label`
 * - `<ChatInput.StopButton>` — opt-in circular `square` stop control (`onClick`, `label`). Rendering it replaces the default Send. Stays clickable when root `disabled` is set for streaming.
 * - `<ChatInput.Footnote>` — disclaimer or error text below the card
 * - `<ChatInput.DropOverlay>` — drag overlay; only renders when `fileDrop` is set and a drag is active
 *
 * ### Accessibility
 * - `onFocusChange` fires only for textarea focus, not toolbar buttons, so menus and send controls don't accidentally trigger focus-mode overlays
 * - With `collapseToolbarWhenShort`, on a short viewport (the `short` variant: under 30rem tall, as at 400% zoom) the toolbar row stays hidden until focus is inside the card, including a menu opened from it, so a transcript above keeps room to read (WCAG 1.4.10). It stays shown while `<ChatInput.StopButton>` is rendered
 * - `animatedPlaceholder.ariaDescription` overrides the auto-derived SR-only description (defaults to the first 3 prompts); only needed when the prompt text itself makes a poor description
 * - The animated overlay is hidden via `motion-reduce:hidden` when `prefers-reduced-motion` is set; the native `placeholder` on `<ChatInput.Textarea>` is shown instead
 * - Drag-and-drop status is announced via an `aria-live` region; localize `fileDrop.text` and `<ChatInput.DropOverlay>` copy
 *
 * ```tsx
 * <ChatInput shadow="elevated" onFocusChange={setFocused}>
 *   <ChatInput.Textarea value={value} onChange={(e) => setValue(e.target.value)} placeholder="Ask anything..." />
 *   <ChatInput.Attachments label={`${files.length} files`}>{chips}</ChatInput.Attachments>
 *   <ChatInput.Menu>
 *     <ChatInput.MenuItem icon="paperclip" label="Add files" onSelect={openPicker} />
 *   </ChatInput.Menu>
 *   <ChatInput.SendButton disabled={!value.trim()} onClick={handleSend} />
 * </ChatInput>
 * ```
 */
export const ChatInput = Object.assign(ChatInputBase, {
  Context: ChatInputContextSlot,
  LeadingAdornment: ChatInputLeadingAdornmentSlot,
  Textarea: ChatInputTextareaSlot,
  Attachments: ChatInputAttachmentsSlot,
  Menu: ChatInputMenuSlot,
  MenuItem: ChatInputMenuItem,
  ToolbarStart: ChatInputToolbarStartSlot,
  ToolbarEnd: ChatInputToolbarEndSlot,
  SendButton: ChatInputSendButtonSlot,
  StopButton: ChatInputStopButtonSlot,
  Footnote: ChatInputFootnoteSlot,
  DropOverlay: ChatInputDropOverlaySlot,
});
